// ClinicOS Portal — Patient Telehealth Join Page
// Daily.co embedded video for patient side

import { useState, useEffect } from 'react';
import { Video, Phone, PhoneOff, Mic, MicOff, Loader2 } from 'lucide-react';
import { useParams } from 'react-router-dom';

export function TelehealthPage() {
    const { appointmentId } = useParams<{ appointmentId: string }>();
    const [roomUrl, setRoomUrl] = useState<string | null>(null);
    const [joined, setJoined] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [audioOn, setAudioOn] = useState(true);

    useEffect(() => {
        if (!appointmentId) return;
        fetch(`/api/telehealth/rooms/${appointmentId}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error('Room not found')))
            .then(data => { setRoomUrl(data.data?.url || null); setLoading(false); })
            .catch(() => { setError('No se encontró la sala de video.'); setLoading(false); });
    }, [appointmentId]);

    if (loading) {
        return (
            <div className="fade-in" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
                <div style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', marginBottom: 8 }} />
                    <p>Cargando teleconsulta...</p>
                </div>
            </div>
        );
    }

    if (error || !roomUrl) {
        return (
            <div className="fade-in">
                <div className="portal-header">
                    <h1>Teleconsulta</h1>
                    <p>Videollamada con su médico</p>
                </div>
                <div className="portal-content">
                    <div className="portal-card" style={{ textAlign: 'center', padding: 32 }}>
                        <Video size={48} style={{ color: 'var(--color-text-muted)', marginBottom: 12, opacity: 0.4 }} />
                        <p style={{ color: 'var(--color-text-muted)' }}>{error || 'No hay teleconsulta activa.'}</p>
                        <p style={{ color: 'var(--color-text-muted)', fontSize: 12, marginTop: 8 }}>
                            Su médico le enviará un enlace por WhatsApp cuando inicie la llamada.
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!joined) {
        return (
            <div className="fade-in">
                <div className="portal-header">
                    <h1>Teleconsulta</h1>
                    <p>Su cita por videollamada está lista</p>
                </div>
                <div className="portal-content">
                    <div className="portal-card" style={{ textAlign: 'center', padding: 32 }}>
                        <Video size={48} style={{ color: 'var(--color-primary)', marginBottom: 16 }} />
                        <h3 style={{ marginBottom: 8 }}>Sala de Video Lista</h3>
                        <p style={{ color: 'var(--color-text-muted)', marginBottom: 20 }}>
                            Asegúrese de tener buena conexión a internet, cámara y micrófono activados.
                        </p>
                        <button onClick={() => setJoined(true)} style={{
                            background: 'var(--color-primary)', color: '#fff', border: 'none', padding: '14px 32px',
                            borderRadius: 12, fontSize: 16, fontWeight: 600, cursor: 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: 8,
                        }}>
                            <Phone size={18} /> Unirse a la Llamada
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: '#000' }}>
            <iframe
                src={`${roomUrl}?showLeaveButton=false`}
                allow="camera; microphone; fullscreen; display-capture"
                style={{ width: '100%', height: '100%', border: 'none' }}
                title="Teleconsulta"
            />
            <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0, padding: 16,
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
                display: 'flex', justifyContent: 'center', gap: 12,
            }}>
                <button onClick={() => setAudioOn(!audioOn)} style={{
                    width: 48, height: 48, borderRadius: '50%', border: 'none',
                    background: audioOn ? 'rgba(255,255,255,0.2)' : '#ef4444',
                    color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {audioOn ? <Mic size={20} /> : <MicOff size={20} />}
                </button>
                <button onClick={() => { setJoined(false); }} style={{
                    width: 64, height: 48, borderRadius: 24, border: 'none', background: '#ef4444',
                    color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <PhoneOff size={20} />
                </button>
            </div>
        </div>
    );
}
